import type {
  RoofMarketplaceBidRequest,
  UserPoliciesList,
} from "@keystone-ui/types";

import { EMAIL_INVALID_MESSAGE, isEmailFormatValid } from "consts";
import {
  type KeystoneInspection,
  type KeystoneInspections,
  type KeystoneReferral,
  type PolicyDetail,
} from "data/keystone-api-mapping";

type SelectedPolicy = UserPoliciesList[number];

/** Lowercase phrase that must appear in `actionStatement` (substring, not whole-string equality). */
const FULL_ROOF_REPLACEMENT_PHRASE = "full roof replacement";

export type {
  RoofMarketplaceBidRequest,
  RoofMarketplaceContactBlock,
} from "@keystone-ui/types";

export type BuildRoofMarketplaceBidRequestInput = {
  policyDetail: PolicyDetail | undefined;
  inspectionsData: KeystoneInspections | undefined;
  /** Fallback policy id (e.g. platform policy id) when current term has no policy number. */
  selectedPolicy?: SelectedPolicy | undefined;
  /** Logged-in user (SS_DATA) — point-of-contact first / last name. */
  user: { firstName: string; lastName: string; email: string } | undefined;
  formEmail: string;
  formPhone: string;
};

function getLatestInspection(
  inspectionsData: KeystoneInspections | undefined,
): KeystoneInspection | undefined {
  if (!inspectionsData?.inspections?.length) {
    return undefined;
  }
  return [...inspectionsData.inspections].sort(
    (a, b) =>
      new Date(b.createdTimestamp).getTime() -
      new Date(a.createdTimestamp).getTime(),
  )[0];
}

function normalizeActionStatement(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function normalizeCategory(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function locationHasMainDwelling(locationDetail: string | undefined): boolean {
  return (locationDetail ?? "").toLowerCase().includes("main dwelling");
}

/**
 * Roof write-out qualifies for RoofMarketplace when the referral is roof-related
 * (category case-insensitive), location mentions the main dwelling, and
 * `actionStatement` contains a full-roof-replacement phrase (substring match,
 * case-insensitive).
 */
export function isQualifyingRoofMarketplaceReferral(
  referral: KeystoneReferral,
): boolean {
  if (normalizeCategory(referral.category) !== "roof") {
    return false;
  }
  if (!locationHasMainDwelling(referral.locationDetail)) {
    return false;
  }
  const action = normalizeActionStatement(referral.actionStatement);
  if (!action || !action.includes(FULL_ROOF_REPLACEMENT_PHRASE)) {
    return false;
  }
  return true;
}

/**
 * True when the latest inspection includes at least one qualifying RoofMarketplace referral.
 */
export function isRoofMarketplaceEligible(
  inspectionsData: KeystoneInspections | undefined,
): boolean {
  const latest = getLatestInspection(inspectionsData);
  const referrals = latest?.review?.referrals ?? [];
  return referrals.some(isQualifyingRoofMarketplaceReferral);
}

const MAIN_DWELLING_STRUCTURE_LABEL = "Main dwelling";

/**
 * Single "House" structure label from qualifying referrals on the latest inspection.
 * Sends {@link MAIN_DWELLING_STRUCTURE_LABEL} when any qualifying location mentions
 * the main dwelling; otherwise falls back to "House".
 */
function houseStructureLabelFromInspections(
  inspectionsData: KeystoneInspections | undefined,
): string {
  const latest = getLatestInspection(inspectionsData);
  const qualifying = (latest?.review?.referrals ?? []).filter(
    isQualifyingRoofMarketplaceReferral,
  );
  if (!qualifying.length) {
    return "House";
  }
  if (qualifying.some((r) => locationHasMainDwelling(r.locationDetail))) {
    return MAIN_DWELLING_STRUCTURE_LABEL;
  }
  return "House";
}

const US_PHONE_DIGITS_MIN = 10;

const EMAIL_FIELD_EMPTY_MESSAGE =
  "Enter the email address where you'd like to receive bids.";

const PHONE_FIELD_INVALID_MESSAGE = "Please enter a valid phone number.";

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/** RoofMarketplace example format: "(414)350-1234" (no space after `)`). */
function formatUsPhoneForPayload(digits: string): string {
  let d = digitsOnly(digits);
  if (d.length === 11 && d.startsWith("1")) {
    d = d.slice(1);
  }
  d = d.slice(0, 10);
  if (d.length !== 10) {
    return (digits ?? "").trim();
  }
  return `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6)}`;
}

function phoneEntry(number: string | undefined | null) {
  const d = digitsOnly(number ?? "");
  if (d.length >= US_PHONE_DIGITS_MIN) {
    return [{ number: formatUsPhoneForPayload(d) }];
  }
  const n = (number ?? "").trim();
  return n ? [{ number: n }] : [];
}

export type RoofMarketplaceFieldErrors = {
  email?: string;
  phone?: string;
};

export type RoofMarketplaceBuildResult =
  | { ok: true; payload: RoofMarketplaceBidRequest }
  | {
      ok: false;
      message: string;
      fieldErrors?: Partial<RoofMarketplaceFieldErrors>;
      /** When false, only inline `fieldErrors` apply (no top validation `Notification`). */
      showBanner: boolean;
    };

/** Bid API failure copy for the danger `Notification` (variant banner). */
export const ROOF_MARKETPLACE_BID_FAILED = {
  title: "We couldn't send your bid request.",
  description:
    "Please try again, or reach out to your representative if the problem continues.",
} as const;

/** Validation failure — `Notification` title; description is the specific validation message. */
export const ROOF_MARKETPLACE_VALIDATION_TITLE =
  "We can't submit your request yet";

/**
 * Like {@link buildRoofMarketplaceBidRequest} but returns the first validation error message
 * for user-facing messaging when required policy, user, or form data is missing or invalid.
 */
export function buildRoofMarketplaceBidRequestResult(
  input: BuildRoofMarketplaceBidRequestInput,
): RoofMarketplaceBuildResult {
  const {
    policyDetail,
    inspectionsData,
    selectedPolicy,
    user,
    formEmail,
    formPhone,
  } = input;
  const details = policyDetail?.userDetails;
  const property =
    details?.propertyAddress ?? selectedPolicy?.propertyAddress ?? undefined;
  const term = details?.currentTerm;
  const pi = details?.primaryInsured;

  const line1 = (property?.street1 ?? "").trim();
  const line2 = (property?.street2 ?? "").trim();
  const city = property?.city?.trim() ?? "";
  const state = property?.state?.trim() ?? "";
  const zip = property?.zip?.trim() ?? "";
  const policyNumber =
    term?.policyNumber?.trim() ||
    selectedPolicy?.platformPolicyId?.trim() ||
    "";
  const formEmailTrimmed = formEmail.trim();
  const formPhoneDigits = digitsOnly(formPhone);
  const homeownerEmail =
    (pi?.emailAddress ?? "").trim() ||
    (details?.insuredPreferences?.opInsuredEmailAddress ?? "").trim() ||
    (user?.email ?? "").trim();

  const validationSteps: {
    ok: boolean;
    message: string;
    field?: keyof RoofMarketplaceFieldErrors;
  }[] = [
    {
      ok: (!!line1 || !!line2) && !!city && !!state && !!zip,
      message:
        "We need a complete property address (street, city, state, and ZIP). If anything is missing, update your property in Settings or contact your agent.",
    },
    {
      ok: !!policyNumber,
      message:
        "We couldn't find a policy number for this policy. Please try again later or contact support.",
    },
    {
      ok: !!(pi?.firstName?.trim() && pi?.lastName?.trim()),
      message:
        "The primary insured name on this policy is incomplete. Contact support if you need help updating your policy.",
    },
    {
      ok: !!(user?.firstName?.trim() && user?.lastName?.trim()),
      message:
        "Your account is missing a first or last name. Add your name in Settings, then try again.",
    },
    {
      ok: !!formEmailTrimmed && isEmailFormatValid(formEmailTrimmed),
      message: !formEmailTrimmed
        ? EMAIL_FIELD_EMPTY_MESSAGE
        : EMAIL_INVALID_MESSAGE,
      field: "email",
    },
    {
      ok: formPhoneDigits.length >= US_PHONE_DIGITS_MIN,
      message: PHONE_FIELD_INVALID_MESSAGE,
      field: "phone",
    },
    {
      ok: !!homeownerEmail,
      message:
        "We couldn't find an email on file for the primary insured. Update contact information in Settings or contact your agent.",
    },
    {
      ok: digitsOnly(pi?.phoneNumber ?? "").length >= US_PHONE_DIGITS_MIN,
      message:
        "We couldn't find a valid phone number on file for the primary insured. Update contact information in Settings or contact your agent.",
    },
  ];

  const fieldErrors: Partial<RoofMarketplaceFieldErrors> = {};
  const bannerMessages: string[] = [];

  for (const step of validationSteps) {
    if (step.ok) {
      continue;
    }
    if (step.field === "email") {
      fieldErrors.email = step.message;
    } else if (step.field === "phone") {
      fieldErrors.phone = step.message;
    } else {
      bannerMessages.push(step.message);
    }
  }

  if (bannerMessages.length > 0 || fieldErrors.email || fieldErrors.phone) {
    const showBanner = bannerMessages.length > 0;
    const message =
      bannerMessages[0] ??
      fieldErrors.email ??
      fieldErrors.phone ??
      "We couldn't complete your request.";
    return {
      ok: false,
      message,
      fieldErrors:
        fieldErrors.email || fieldErrors.phone ? fieldErrors : undefined,
      showBanner,
    };
  }

  if (!pi || !user) {
    return {
      ok: false,
      message: `${ROOF_MARKETPLACE_BID_FAILED.title} Please try again later.`,
      showBanner: true,
    };
  }

  const formPhoneForPayload = formatUsPhoneForPayload(formPhoneDigits);
  const homeownerPhones = phoneEntry(pi.phoneNumber);
  const structureLabel = houseStructureLabelFromInspections(inspectionsData);
  const pointOfContactPhones = phoneEntry(formPhoneForPayload);
  const roofCoveringType =
    (policyDetail?.userPropertyProfileData?.roofCoveringType ?? "").trim() ||
    "0";

  return {
    ok: true,
    payload: {
      type: "UNDERWRITING",
      address: {
        line1,
        line2,
        city,
        state,
        zip,
      },
      policy: {
        number: policyNumber,
      },
      homeowner: {
        firstName: (pi.firstName ?? "").trim(),
        lastName: (pi.lastName ?? "").trim(),
        emailAddress: homeownerEmail,
        phoneNumbers: homeownerPhones,
      },
      pointOfContact: {
        firstName: user.firstName.trim(),
        lastName: user.lastName.trim(),
        emailAddress: formEmailTrimmed,
        phoneNumbers: pointOfContactPhones,
      },
      structures: [
        {
          label: structureLabel,
          roofSystems: [{ roofCoveringType }],
        },
      ],
    },
  };
}

/**
 * Assembles the RoofMarketplace bid request from policy, inspections, user, and form values.
 * Returns `null` if required data is missing.
 */
export function buildRoofMarketplaceBidRequest(
  input: BuildRoofMarketplaceBidRequestInput,
): RoofMarketplaceBidRequest | null {
  const result = buildRoofMarketplaceBidRequestResult(input);
  return result.ok ? result.payload : null;
}
